import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Star, Clock, Users, Share2, Send, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo, getDiscordAvatarUrl } from '../lib/timeUtils';
import { fetchPost, likePost, fetchPostComments, createComment, deleteComment } from '../lib/api';
import ConfirmModal from '../components/ConfirmModal';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  const showModal = (config) => {
    setModalConfig({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      onConfirm: config.onConfirm || null
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const postData = await fetchPost(postId);
        setPost(postData);
        setIsLiked(postData.is_liked || false);
        setLikesCount(postData.likes || 0);
      } catch (err) {
        console.error('Erro ao carregar post:', err);
        setError('Post não encontrado');
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      loadPost();
    }
  }, [postId]);

  // Carregar comentários quando o post estiver disponível
  useEffect(() => {
    const loadComments = async () => {
      if (!postId || loading) return;
      
      try {
        setCommentsLoading(true);
        const commentsData = await fetchPostComments(postId);
        setComments(commentsData || []);
      } catch (err) {
        console.error('Erro ao carregar comentários:', err);
        setComments([]);
      } finally {
        setCommentsLoading(false);
      }
    };

    loadComments();
  }, [postId, loading]);

  const handleLike = async () => {
    if (liking) return;
    
    try {
      setLiking(true);
      const newIsLiked = !isLiked;
      setIsLiked(newIsLiked);
      setLikesCount(prev => newIsLiked ? prev + 1 : Math.max(prev - 1, 0));
      
      const response = await likePost(postId);
      setIsLiked(response.is_liked);
      setLikesCount(response.likes);
    } catch (err) {
      console.error('Erro ao curtir post:', err);
      // Reverter mudança otimista
      setIsLiked(!isLiked);
      setLikesCount(prev => isLiked ? prev + 1 : Math.max(prev - 1, 0));
    } finally {
      setLiking(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;
    
    try {
      setSubmittingComment(true);
      const response = await createComment(postId, newComment);
      
      if (response.comment) {
        // Adicionar o novo comentário à lista
        setComments([...comments, response.comment]);
        setNewComment('');
      }
    } catch (err) {
      console.error('Erro ao criar comentário:', err);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao criar comentário. Tente novamente.'
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    showModal({
      type: 'confirm',
      title: 'Confirmar exclusão',
      message: 'Tem certeza que deseja excluir este comentário?',
      onConfirm: async () => {
        try {
          await deleteComment(commentId);
          setComments(comments.filter(c => c.id !== commentId));
        } catch (err) {
          console.error('Erro ao deletar comentário:', err);
          showModal({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao deletar comentário. Tente novamente.'
          });
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error || 'Post não encontrado'}</p>
          <Link to="/feed" className="text-cyan-400 hover:text-cyan-300">
            Voltar ao Feed
          </Link>
        </div>
      </div>
    );
  }

  const authorAvatar = post.author_avatar 
    ? getDiscordAvatarUrl(post.author_discord_id, post.author_avatar)
    : getDiscordAvatarUrl(post.author_discord_id || '0', null);
  // Fallback para avatar padrão se a imagem falhar
  const defaultAuthorAvatar = getDiscordAvatarUrl(post.author_discord_id || '0', null);

  const userAvatar = user?.discord_avatar
    ? getDiscordAvatarUrl(user.discord_id, user.discord_avatar)
    : getDiscordAvatarUrl(user?.discord_id || '0', null);

  // Banner/backdrop do filme
  const backdropUrl = post.movie_data?.backdrop_path 
    ? `https://image.tmdb.org/t/p/w1280${post.movie_data.backdrop_path}`
    : post.movie_poster || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white">
      
      {/* Banner do Filme */}
      {backdropUrl && (
        <div className="relative h-96 w-full overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={backdropUrl} 
              alt={post.movie_title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-slate-950"></div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-8">
        
        {/* Botão Voltar */}
        <Link 
          to="/feed"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span>Voltar ao Feed</span>
        </Link>

        {/* Conteúdo Principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Sidebar - Informações do Filme */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {post.movie_poster && (
                <div className="mb-6">
                  <img 
                    src={post.movie_poster} 
                    alt={post.movie_title}
                    className="w-full rounded-lg shadow-2xl border border-white/20"
                  />
                </div>
              )}
              
              <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-lg p-6">
                <h1 className="text-2xl font-black mb-2">{post.movie_title}</h1>
                {post.movie_year && (
                  <p className="text-gray-400 mb-4">{post.movie_year}</p>
                )}
                
                {post.rating && (
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="text-yellow-400 fill-yellow-400" size={20} />
                    <span className="text-xl font-bold text-yellow-400">
                      {parseFloat(post.rating).toFixed(1)}/10
                    </span>
                    <span className="text-gray-500 text-sm">pelo autor</span>
                  </div>
                )}

                {post.movie_data?.vote_average && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Avaliação TMDb</p>
                    <div className="flex items-center gap-2">
                      <Star className="text-yellow-400 fill-yellow-400" size={16} />
                      <span className="text-sm font-bold">{post.movie_data.vote_average.toFixed(1)}/10</span>
                    </div>
                  </div>
                )}

                {post.movie_data?.genres && Array.isArray(post.movie_data.genres) && post.movie_data.genres.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Gêneros</p>
                    <div className="flex flex-wrap gap-2">
                      {post.movie_data.genres.map((genre, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-white/10 rounded-full text-xs"
                        >
                          {typeof genre === 'string' ? genre : genre.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {post.movie_data?.overview && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-xs text-gray-500 mb-2">Sinopse</p>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {post.movie_data.overview}
                    </p>
                  </div>
                )}

                {post.movie_data?.runtime && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-xs text-gray-500 mb-1">Duração</p>
                    <p className="text-sm text-gray-300">
                      {Math.floor(post.movie_data.runtime / 60)}h {post.movie_data.runtime % 60}min
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content - Avaliação */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Card de Avaliação */}
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden">
              
              {/* Header do Autor */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src={authorAvatar} 
                      alt={post.author_username}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/perfil/${post.author_id}`);
                      }}
                      className="w-14 h-14 rounded-full border-2 border-cyan-500 cursor-pointer hover:border-cyan-400 transition-colors"
                      onError={(e) => {
                        // Se a imagem falhar, usar avatar padrão
                        e.target.src = defaultAuthorAvatar;
                      }}
                    />
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/perfil/${post.author_id}`);
                      }}
                      className="cursor-pointer"
                    >
                      <p className="font-bold text-lg hover:text-cyan-400 transition-colors">{post.author_username}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Clock size={14} />
                        <span>{formatTimeAgo(post.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Conteúdo da Avaliação */}
              <div className="p-6">
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-200 leading-relaxed text-lg whitespace-pre-wrap">
                    {post.review}
                  </p>
                </div>

                {/* Attendees */}
                {post.attendees && Array.isArray(post.attendees) && post.attendees.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <Users size={16} className="text-cyan-400" />
                      <span className="text-gray-400">Assistiram juntos:</span>
                      {post.attendees.map((attendee, idx) => {
                        const attendeeName = typeof attendee === 'string' 
                          ? attendee 
                          : (attendee.username || attendee.discord_username || 'Usuário');
                        const attendeeId = typeof attendee === 'object' && attendee.id ? attendee.id : null;
                        return (
                          <span
                            key={idx}
                            onClick={(e) => {
                              if (attendeeId) {
                                e.stopPropagation();
                                navigate(`/perfil/${attendeeId}`);
                              }
                            }}
                            className={`text-cyan-400 ${attendeeId ? 'hover:underline cursor-pointer' : ''}`}
                          >
                            @{attendeeName}
                            {idx < post.attendees.length - 1 && ','}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Ações */}
              <div className="border-t border-white/10 px-6 py-4 flex items-center gap-6">
                <button 
                  onClick={handleLike}
                  disabled={liking}
                  className="flex items-center gap-2 text-sm hover:text-red-400 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Heart 
                    size={20} 
                    className={`${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-400 group-hover:text-red-400'} transition-colors`}
                  />
                  <span className={isLiked ? 'text-red-500 font-bold' : 'text-gray-400'}>
                    {likesCount}
                  </span>
                </button>
                
                <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors">
                  <MessageCircle size={20} />
                  <span>{comments.length}</span>
                </button>
                
                <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-fuchsia-400 transition-colors">
                  <Share2 size={20} />
                  <span>Compartilhar</span>
                </button>
              </div>
            </div>

            {/* Seção de Comentários */}
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xl font-black flex items-center gap-2">
                  <MessageCircle className="text-cyan-400" size={24} />
                  Comentários
                  <span className="text-sm font-normal text-gray-400">({comments.length})</span>
                </h2>
              </div>

              {/* Formulário de Comentário */}
              <div className="p-6 border-b border-white/10">
                <form onSubmit={handleSubmitComment} className="flex gap-4">
                  <img 
                    src={userAvatar} 
                    alt="avatar"
                    className="w-10 h-10 rounded-full border-2 border-cyan-500 flex-shrink-0"
                  />
                  <div className="flex-1 flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Escreva um comentário..."
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-cyan-500/50 transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={!newComment.trim() || submittingComment}
                      className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-fuchsia-600 rounded-lg font-bold hover:shadow-[0_0_20px_rgba(34,211,238,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Send size={18} />
                      <span>{submittingComment ? 'Enviando...' : 'Enviar'}</span>
                    </button>
                  </div>
                </form>
              </div>

              {/* Lista de Comentários */}
              <div className="divide-y divide-white/10">
                {commentsLoading ? (
                  <div className="p-8 text-center text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
                    <p>Carregando comentários...</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Nenhum comentário ainda. Seja o primeiro a comentar!</p>
                  </div>
                ) : (
                  comments.map((comment) => {
                    const commentAvatar = comment.author_avatar
                      ? getDiscordAvatarUrl(comment.author_discord_id, comment.author_avatar)
                      : getDiscordAvatarUrl(comment.author_discord_id || '0', null);
                    const defaultCommentAvatar = getDiscordAvatarUrl(comment.author_discord_id || '0', null);
                    const isOwnComment = comment.user_id === user?.id;
                    
                    return (
                      <div key={comment.id} className="p-6 hover:bg-white/5 transition-colors">
                        <div className="flex gap-4">
                          <img 
                            src={commentAvatar} 
                            alt={comment.author_username}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (comment.user_id) {
                                navigate(`/perfil/${comment.user_id}`);
                              }
                            }}
                            className="w-10 h-10 rounded-full border-2 border-purple-500 flex-shrink-0 cursor-pointer hover:border-cyan-500 transition-colors"
                            onError={(e) => {
                              // Se a imagem falhar, usar avatar padrão
                              e.target.src = defaultCommentAvatar;
                            }}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (comment.user_id) {
                                    navigate(`/perfil/${comment.user_id}`);
                                  }
                                }}
                                className="cursor-pointer"
                              >
                                <p className="font-bold text-sm hover:text-cyan-400 transition-colors">{comment.author_username}</p>
                                <p className="text-xs text-gray-500">
                                  {formatTimeAgo(comment.created_at)}
                                </p>
                              </div>
                              {isOwnComment && (
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="text-gray-400 hover:text-red-400 transition-colors"
                                  title="Excluir comentário"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                            <p className="text-gray-200 leading-relaxed">{comment.content}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Modal de confirmação/erro */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default PostDetail;

